import React from "react"
import styled from "styled-components"
import { HeadingOne, HeadingThree, Paragraph } from "buoy-core"
import { injectIntl, defineMessages } from "react-intl"
import { fontSize, responsive, fontWeight, colors } from "buoy-theme"
import { responsive as commonResponsive } from "../../common/styledHelpers"
import Text from "../../common/components/Text"

const BannerSection = styled.section`
  ${props => !props.neutral && `background: #efefef`};
  padding-top: 50px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  // NOTE: Margin and padding for COVID-19 banner specific use case
  ${props =>
    props.forCovid19
      ? responsive(
          "margin",
          "77px 0 -67px",
          "77px 0 -67px",
          "67px 0 -60px",
          "27px 0 -60px",
          "57px 0 -60px"
        )
      : responsive(
          "margin",
          "67px 0 -67px",
          "67px 0 -67px",
          "57px 0 -60px",
          "57px 0 -60px",
          "57px 0 -60px"
        )}
  ${props =>
    props.forCovid19
      ? responsive(
          "padding",
          "0 32px",
          "0 32px",
          "0 32px",
          "60px 16px 0 16px",
          "60px 16px 0 16px"
        )
      : responsive(
          "padding",
          "0 32px",
          "0 32px",
          "0 32px",
          "0 32px",
          "0 16px"
        )}
  overflow: auto;
  ${props =>
    !props.show &&
    `
    opacity: 0;
    max-height: 0;
    transition: all 0.5s ease-out;
  `};
`

const Container = styled.div`
  ${responsive("margin", "-20px -20px 32px", "-20px 0 24px")};
  max-width: 496px;
  width: 100%;
`

const EmergencyBox = styled.div`
  ${props =>
    props.neutral
      ? responsive(
          "margin",
          "100px 0 0",
          "100px 0 0",
          "90px 0 0",
          "90px 0 0",
          "24px 0 0"
        )
      : responsive(
          "margin",
          "100px 0 16px",
          "100px 0 16px",
          "90px 0 16px",
          "90px 0 16px",
          "24px 0 16px"
        )};
  background-color: ${colors.neutralLightest};
  border-radius: 4px;
  width: 100%;
  padding: 10px 14px;
`

const EmergencyText = styled(Text)`
  align-items: center;
  text-align: left;
  width: 100%;
  padding: 16px 0;
  color: ${colors.neutralDark};
  font-weight: ${fontWeight.regular}
  letter-spacing: 0.2px;
  font-size: ${commonResponsive("font-size", "16px", "12px")}
`

const Heading = styled(HeadingOne)`
  ${props =>
    props.neutral
      ? responsive("margin-bottom", "0", "0", "0", "0", "0")
      : responsive("margin-bottom", "16px", "16px", "16px", "16px", "16px")};
  ${fontSize("h2")};
  padding: 0;
  max-width: 496px;
  ${responsive("width", "80%", "80%", "80%", "80%", "100%")};
`

const SubHeader = styled(HeadingThree)`
  margin: 0 0 4px;
  padding: 0;
  max-width: 496px;
  ${responsive("width", "80%", "80%", "80%", "80%", "100%")};
  font-weight: ${fontWeight.regular};
`

const BodyText = styled(Paragraph)`
  padding: 0;
  max-width: 496px;
  ${responsive("width", "80%", "80%", "80%", "80%", "100%")};
  margin-bottom: 4px;
`

const FooterText = styled(Paragraph)`
  ${fontSize("bodySmall")};
  ${responsive(
    "margin",
    "12px 0 10px",
    "12px 0 10px",
    "8px 0 10px",
    "8px 0 10px",
    "8px 0 0"
  )};
  ${responsive("width", "80%", "80%", "80%", "80%", "100%")};
  padding: 0;
  max-width: 496px;
`

const bannerMessages = defineMessages({
  simpleFlowHeader: {
    id: "interview.header",
    defaultMessage: "{simpleFlowHeader}"
  },
})

const simpleBanner = (startLanguage, formatMessage) => {
  return (
    <React.Fragment>
      {startLanguage.disclaimer}
      <Heading alignLeft>
        {formatMessage(bannerMessages.simpleFlowHeader, { startLanguage })}
      </Heading>
    </React.Fragment>
  )
}

const noFooterBanner = (simpleFlowHeader, formatMessage) => {
  return (
    <React.Fragment>
      <Heading neutral alignLeft>
        {formatMessage(bannerMessages.simpleFlowHeader, { simpleFlowHeader })}
      </Heading>
    </React.Fragment>
  )
}

const defaultBanner = startLanguage => {
  return (
    <React.Fragment>
      {startLanguage.header() && (
        <Heading alignLeft>{startLanguage.header()}</Heading>
      )}
      {startLanguage.subheader() && (
        <SubHeader alignLeft>{startLanguage.subheader()}</SubHeader>
      )}
      {startLanguage.body1() && (
        <BodyText alignLeft>{startLanguage.body1()}</BodyText>
      )}
      {startLanguage.body2() && (
        <BodyText alignLeft>{startLanguage.body2()}</BodyText>
      )}
      {startLanguage.body3() && (
        <BodyText alignLeft>{startLanguage.body3()}</BodyText>
      )}
      {startLanguage.footer() && (
        <FooterText alignLeft>{startLanguage.footer()}</FooterText>
      )}
    </React.Fragment>
  )
}

const Banner = ({ startLanguage, show, forCovid19, simpleFlowDiagnosis, intl }) => {
  const { formatMessage } = intl
  const simpleFlowHeader = startLanguage.header(simpleFlowDiagnosis && "simple", simpleFlowDiagnosis)
  let neutral = false
  let banner = defaultBanner(startLanguage)
  if (typeof startLanguage === "string") {
    banner = simpleBanner(startLanguage, formatMessage)
    neutral = true
  } else if (!startLanguage.footer()) {
    banner = noFooterBanner(simpleFlowHeader, formatMessage)
    neutral = true
  }
  return (
    <BannerSection
      id="bannerSection"
      neutral={neutral}
      show={show}
      forCovid19={forCovid19}
    >
      <Container>
        <EmergencyBox neutral={neutral}>
          <EmergencyText>{startLanguage.disclaimer()}</EmergencyText>
        </EmergencyBox>
        {banner}
      </Container>
    </BannerSection>
  )
}

Banner.defaultProps = {
  startLanguage: "You're in the right place. Let's get you feeling better.",
}

export default injectIntl(Banner)



// WEBPACK FOOTER //
// ./assets/nav/components/Banner.js